import * as yup from "yup";
import { type CrownEnv } from "../../components/crown-data-import/env/env-config";
import {
  type Edi214ShipmentStatus,
  EdiOutboundPayloadDestination,
  InvoiceTransmissionMethod,
  type SaveTenediPartnershipInput,
  type TenediCarrierIdentityFieldsFragment,
  type TenediCustomerIdentityFieldsFragment,
  type TenediPartnershipFieldsFragment,
  type UpdateTenediCarrierIdentityInput,
  type UpdateTenediCustomerIdentityInput,
} from "../../generated/graphql";
import { PRODUCTION_CONFIRMATION_PHRASE } from "../outbound-test-bench/bench-run";
import { DEPRECATED_214_STATUSES } from "./edi-214-groups";

/*
 * These schemas mirror the backend DTOs (`tenedi-connections.inputs.ts`) and
 * the validation service, so the operator sees a problem before saving. The
 * backend repeats every check and stays the source of truth
 * (20_validation_parity).
 */

const EDI_ID_PATTERN = /^[A-Za-z0-9 !"&'()+,\-./;?=]*$/;
const SCAC_PATTERN = /^([A-Z0-9]{2,4})?$/;
const NO_SPACES_PATTERN = /^\S*$/;

const EDI_ID_MESSAGE =
  "Use up to 15 letters, digits, spaces and punctuation such as - . /, but not * or :.";
const SCAC_MESSAGE = "A SCAC is 2 to 4 letters or digits.";
const STEDI_MESSAGE = "A Stedi partner ID has no spaces.";

/** Trims, so the pattern checks what the backend will store. */
const trimmed = () =>
  yup
    .string()
    .defined()
    .transform((value: string) => value.trim());

const ediIdSchema = trimmed()
  .max(15, EDI_ID_MESSAGE)
  .matches(EDI_ID_PATTERN, EDI_ID_MESSAGE);

const scacSchema = trimmed()
  .transform((value: string) => value.toUpperCase())
  .matches(SCAC_PATTERN, SCAC_MESSAGE);

const stediPartnerIdSchema = trimmed()
  .max(100, "Use 100 characters or fewer.")
  .matches(NO_SPACES_PATTERN, STEDI_MESSAGE);

export type CarrierFormValues = {
  isaId: string;
  alternateIsaId: string;
  secondAlternateIsaId: string;
  thirdAlternateIsaId: string;
  fourthAlternateIsaId: string;
  standardCarrierAlphaCode: string;
  alternateStandardCarrierAlphaCode: string;
  secondAlternateScac: string;
  thirdAlternateScac: string;
  fourthAlternateScac: string;
  stediPartnerId: string;
};

export const CARRIER_ISA_FIELDS = [
  "isaId",
  "alternateIsaId",
  "secondAlternateIsaId",
  "thirdAlternateIsaId",
  "fourthAlternateIsaId",
] as const satisfies ReadonlyArray<keyof CarrierFormValues>;

export const CARRIER_SCAC_FIELDS = [
  "standardCarrierAlphaCode",
  "alternateStandardCarrierAlphaCode",
  "secondAlternateScac",
  "thirdAlternateScac",
  "fourthAlternateScac",
] as const satisfies ReadonlyArray<keyof CarrierFormValues>;

const repeatedValues = (values: readonly string[]): string[] => {
  const filled = values.map((value) => value.trim().toUpperCase());
  return filled.filter(
    (value, index) => value !== "" && filled.indexOf(value) !== index,
  );
};

export const carrierFormSchema = yup
  .object({
    isaId: ediIdSchema,
    alternateIsaId: ediIdSchema,
    secondAlternateIsaId: ediIdSchema,
    thirdAlternateIsaId: ediIdSchema,
    fourthAlternateIsaId: ediIdSchema,
    standardCarrierAlphaCode: scacSchema,
    alternateStandardCarrierAlphaCode: scacSchema,
    secondAlternateScac: scacSchema,
    thirdAlternateScac: scacSchema,
    fourthAlternateScac: scacSchema,
    stediPartnerId: stediPartnerIdSchema,
  })
  .test(
    "no-repeated-ids",
    "The same ID appears in two boxes. Keep it in one and clear the other.",
    (values) =>
      repeatedValues(CARRIER_ISA_FIELDS.map((field) => values[field]))
        .length === 0 &&
      repeatedValues(CARRIER_SCAC_FIELDS.map((field) => values[field]))
        .length === 0,
  );

export type CustomerFormValues = {
  isaId: string;
  ediApplicationId: string;
  ediContactCode: string;
  stediPartnerId: string;
  standardCarrierAlphaCode: string;
  defaultInvoiceTransmissionMethod: InvoiceTransmissionMethod;
};

export const customerFormSchema = yup.object({
  isaId: ediIdSchema,
  ediApplicationId: ediIdSchema,
  ediContactCode: trimmed().max(50, "Use 50 characters or fewer."),
  stediPartnerId: stediPartnerIdSchema,
  standardCarrierAlphaCode: scacSchema,
  defaultInvoiceTransmissionMethod: yup
    .mixed<InvoiceTransmissionMethod>()
    .oneOf(Object.values(InvoiceTransmissionMethod))
    .required("Pick an invoice method."),
});

export type PartnershipFormValues = {
  companyStandardCarrierAlphaCode: string;
  ediOutboundPayloadDestination: EdiOutboundPayloadDestination;
  send990InResponseTo204: boolean;
  send990InResponseTo211: boolean;
  autoAcceptOrders: boolean;
  edi214ShipmentStatuses: Edi214ShipmentStatus[];
  canCreateMultipleStops: boolean;
  ignoreCancellations: boolean;
  allowEditingShipperBillOfLadingNumber: boolean;
  ignoreNonEdiOutbound: boolean;
  removeSpacesFrom214Bol: boolean;
  excludeZeroChargeInvoices: boolean;
  sendBackupDocs: boolean;
  alternateStediPartnerId: string;
  enableStaging: boolean;
  useStediGenerateEdiFunction: boolean;
};

/**
 * The partnership schema for one carrier: its SCAC must be one of
 * `carrierScacs` (backend rule 4), and no retired 214 status may be ticked.
 */
export const partnershipFormSchema = (carrierScacs: readonly string[]) =>
  yup.object({
    companyStandardCarrierAlphaCode: scacSchema
      .required("Pick the SCAC this partnership uses.")
      .test(
        "carrier-scac",
        "That SCAC is not one of this carrier's SCACs. Add it under Carrier identity first.",
        // A blank SCAC gets the `required` message instead.
        (value) => value === "" || carrierScacs.includes(value),
      ),
    ediOutboundPayloadDestination: yup
      .mixed<EdiOutboundPayloadDestination>()
      .oneOf(Object.values(EdiOutboundPayloadDestination))
      .required(),
    edi214ShipmentStatuses: yup
      .array()
      .of(yup.mixed<Edi214ShipmentStatus>().required())
      .required()
      .test(
        "no-retired-statuses",
        "Untick the retired line haul statuses.",
        (statuses) =>
          statuses.every((status) => !DEPRECATED_214_STATUSES.has(status)),
      ),
    alternateStediPartnerId: stediPartnerIdSchema,
  });

/**
 * Field-level messages for `values`, keyed by field name. A check on the whole
 * form (such as a repeated id) comes back under `form`.
 */
export const formErrors = (
  schema: yup.AnySchema,
  values: unknown,
): Record<string, string> => {
  try {
    schema.validateSync(values, { abortEarly: false });
    return {};
  } catch (error) {
    if (!(error instanceof yup.ValidationError)) {
      throw error;
    }
    const errors: Record<string, string> = {};
    for (const inner of error.inner.length > 0 ? error.inner : [error]) {
      const key =
        inner.path === undefined || inner.path === "" ? "form" : inner.path;
      errors[key] ??= inner.message;
    }
    return errors;
  }
};

const text = (value: string | null | undefined): string => value ?? "";

const blankToNull = (value: string): string | null => {
  const trimmedValue = value.trim();
  return trimmedValue === "" ? null : trimmedValue;
};

export const carrierFormFromEntity = (
  carrier: TenediCarrierIdentityFieldsFragment,
): CarrierFormValues => ({
  isaId: text(carrier.isaId),
  alternateIsaId: text(carrier.alternateIsaId),
  secondAlternateIsaId: text(carrier.secondAlternateIsaId),
  thirdAlternateIsaId: text(carrier.thirdAlternateIsaId),
  fourthAlternateIsaId: text(carrier.fourthAlternateIsaId),
  standardCarrierAlphaCode: text(carrier.standardCarrierAlphaCode),
  alternateStandardCarrierAlphaCode: text(
    carrier.alternateStandardCarrierAlphaCode,
  ),
  secondAlternateScac: text(carrier.secondAlternateScac),
  thirdAlternateScac: text(carrier.thirdAlternateScac),
  fourthAlternateScac: text(carrier.fourthAlternateScac),
  stediPartnerId: text(carrier.stediPartnerId),
});

/** Sends every field, so a box the operator emptied clears that id. */
export const toCarrierInput = (
  companyUuid: string,
  values: CarrierFormValues,
  confirmProduction: boolean | undefined,
): UpdateTenediCarrierIdentityInput => ({
  companyUuid,
  confirmProduction,
  ...(Object.fromEntries(
    Object.entries(values).map(([field, value]) => [field, blankToNull(value)]),
  ) as Record<keyof CarrierFormValues, string | null>),
});

export const customerFormFromEntity = (
  customer: TenediCustomerIdentityFieldsFragment,
): CustomerFormValues => ({
  isaId: text(customer.isaId),
  ediApplicationId: text(customer.ediApplicationId),
  ediContactCode: text(customer.ediContactCode),
  stediPartnerId: text(customer.stediPartnerId),
  standardCarrierAlphaCode: text(customer.standardCarrierAlphaCode),
  defaultInvoiceTransmissionMethod: customer.defaultInvoiceTransmissionMethod,
});

export const toCustomerInput = (
  ids: { companyUuid: string; organizationUuid: string },
  values: CustomerFormValues,
  confirmProduction: boolean | undefined,
): UpdateTenediCustomerIdentityInput => ({
  ...ids,
  confirmProduction,
  isaId: blankToNull(values.isaId),
  ediApplicationId: blankToNull(values.ediApplicationId),
  ediContactCode: blankToNull(values.ediContactCode),
  stediPartnerId: blankToNull(values.stediPartnerId),
  standardCarrierAlphaCode: blankToNull(values.standardCarrierAlphaCode),
  defaultInvoiceTransmissionMethod: values.defaultInvoiceTransmissionMethod,
});

/**
 * A new partnership: sends to Tenedi, replies to 204s with a 990, accepts
 * tenders automatically, ticks no 214s, and keeps the newer Stedi format off
 * until Tenedi confirms which format it reads. The SCAC starts as the
 * carrier's primary one.
 */
export const newPartnershipForm = (
  carrierScacs: readonly string[],
): PartnershipFormValues => ({
  companyStandardCarrierAlphaCode: carrierScacs[0] ?? "",
  ediOutboundPayloadDestination: EdiOutboundPayloadDestination.Tenedi,
  send990InResponseTo204: true,
  send990InResponseTo211: false,
  autoAcceptOrders: true,
  edi214ShipmentStatuses: [],
  canCreateMultipleStops: false,
  ignoreCancellations: false,
  allowEditingShipperBillOfLadingNumber: false,
  ignoreNonEdiOutbound: false,
  removeSpacesFrom214Bol: false,
  excludeZeroChargeInvoices: true,
  sendBackupDocs: false,
  alternateStediPartnerId: "",
  enableStaging: false,
  useStediGenerateEdiFunction: false,
});

export const partnershipFormFromEntity = (
  partnership: TenediPartnershipFieldsFragment,
): PartnershipFormValues => ({
  companyStandardCarrierAlphaCode: text(
    partnership.companyStandardCarrierAlphaCode,
  ),
  ediOutboundPayloadDestination: partnership.ediOutboundPayloadDestination,
  send990InResponseTo204: partnership.send990InResponseTo204,
  send990InResponseTo211: partnership.send990InResponseTo211,
  autoAcceptOrders: partnership.autoAcceptOrders,
  edi214ShipmentStatuses: [...partnership.edi214ShipmentStatuses],
  canCreateMultipleStops: partnership.canCreateMultipleStops,
  ignoreCancellations: partnership.ignoreCancellations,
  allowEditingShipperBillOfLadingNumber:
    partnership.allowEditingShipperBillOfLadingNumber,
  ignoreNonEdiOutbound: partnership.ignoreNonEdiOutbound,
  removeSpacesFrom214Bol: partnership.removeSpacesFrom214Bol,
  excludeZeroChargeInvoices: partnership.excludeZeroChargeInvoices,
  sendBackupDocs: partnership.sendBackupDocs,
  alternateStediPartnerId: text(partnership.alternateStediPartnerId),
  enableStaging: partnership.enableStaging,
  useStediGenerateEdiFunction: partnership.useStediGenerateEdiFunction,
});

/**
 * Whether saving `values` over `original` changes where the partnership sends,
 * which the backend refuses without `confirmCutover` (rule 7). A new
 * partnership has no cutover.
 */
export const needsCutoverConfirmation = (
  original: TenediPartnershipFieldsFragment | null,
  values: PartnershipFormValues,
): boolean =>
  original !== null &&
  original.ediOutboundPayloadDestination !==
    values.ediOutboundPayloadDestination;

export const toSavePartnershipInput = ({
  ids,
  partnershipUuid,
  values,
  confirmCutover,
  confirmProduction,
}: {
  ids: { companyUuid: string; organizationUuid: string };
  partnershipUuid: string | null;
  values: PartnershipFormValues;
  confirmCutover: boolean;
  confirmProduction: boolean | undefined;
}): SaveTenediPartnershipInput => ({
  ...ids,
  ...values,
  partnershipUuid,
  companyStandardCarrierAlphaCode: values.companyStandardCarrierAlphaCode
    .trim()
    .toUpperCase(),
  alternateStediPartnerId: blankToNull(values.alternateStediPartnerId),
  confirmCutover: confirmCutover ? true : undefined,
  confirmProduction,
});

/**
 * Whether saves need the typed confirmation: the environment picker points
 * at prod, or the backend reports it runs in production. Either alone is
 * enough, so a mislabelled environment cannot skip the confirmation.
 */
export const isProductionTarget = (
  env: CrownEnv,
  serverProduction: boolean | undefined,
): boolean => env === "prod" || serverProduction === true;

/** Whether the page may save: always outside production, and after the typed phrase in it. */
export const isSaveAllowed = (production: boolean, typed: string): boolean =>
  !production || typed.trim() === PRODUCTION_CONFIRMATION_PHRASE;

/** The `confirmProduction` value: true only for a confirmed production save, else omitted. */
export const confirmProductionFor = (
  production: boolean,
  typed: string,
): boolean | undefined =>
  production && isSaveAllowed(production, typed) ? true : undefined;
